import * as client from "../../../client";

export type EffectiveExcerpt = {
  name: string;
  value: string;
  isOverride: boolean;
  /** The form's value an override replaced; null when the excerpt is not
   *  overridden or was added by hand (no extracted value to revert to). */
  original: string | null;
  sortKey: string | null;
};

/** A form excerpt the user removed, still held in `direct_excerpts`. */
export type RemovedExcerpt = {
  name: string;
  value: string;
};

/** Merge a limit/deductible's extracted excerpts with the user's manual overrides
 *  and drop removed names. Mirrors the backend `_merge_excerpts`: names join
 *  case-insensitively, the extracted name is kept for display, and the override
 *  supplies the value. */
export const effectiveExcerpts = (
  entry: client.AdvancedLimit | client.AdvancedDeductible
): EffectiveExcerpt[] => {
  const key = (name: string): string => name.toLowerCase();
  const removed = new Set((entry.removed_excerpt_names ?? []).map(key));
  const overrideKeys = new Set(
    Object.keys(entry.manual_excerpt_overrides ?? {}).map(key)
  );
  const sortKeys = new Map(
    Object.entries(entry.excerpt_sort_keys ?? {}).map(([n, k]) => [key(n), k])
  );

  // Extracted names first, then override-only names (manual additions), matching
  // the backend spread order. Re-setting an existing key keeps its position.
  const merged = new Map<
    string,
    { name: string; value: string; original: string | null }
  >();
  for (const [name, value] of Object.entries(entry.direct_excerpts ?? {})) {
    merged.set(key(name), { name, value, original: null });
  }
  for (const [name, value] of Object.entries(
    entry.manual_excerpt_overrides ?? {}
  )) {
    const extracted = merged.get(key(name));
    merged.set(key(name), {
      name: extracted?.name ?? name,
      value,
      original: extracted?.value ?? null,
    });
  }

  const result: EffectiveExcerpt[] = [];
  for (const [k, { name, value, original }] of merged) {
    // An override wins over a same-name removal, so a case-only rename (remove
    // the old name, re-add it under new casing) keeps the excerpt.
    if (removed.has(k) && !overrideKeys.has(k)) continue;
    result.push({
      name,
      value,
      isOverride: overrideKeys.has(k),
      original,
      sortKey: sortKeys.get(k) ?? null,
    });
  }
  return result;
};

/** The form excerpts `effectiveExcerpts` hides because the user removed them,
 *  in extracted order. An override beats a removal there too, so an excerpt
 *  that is both removed and overridden is not listed. */
export const removedExcerpts = (
  entry: client.AdvancedLimit | client.AdvancedDeductible
): RemovedExcerpt[] => {
  const key = (name: string): string => name.toLowerCase();
  const removed = new Set((entry.removed_excerpt_names ?? []).map(key));
  const overridden = new Set(
    Object.keys(entry.manual_excerpt_overrides ?? {}).map(key)
  );
  return Object.entries(entry.direct_excerpts ?? {})
    .filter(([name]) => removed.has(key(name)) && !overridden.has(key(name)))
    .map(([name, value]) => ({ name, value }));
};
